/**
 * Application constants.
 * Central place for app name, score rules, storage keys, UI copy, and share text.
 * All configuration objects are frozen to prevent accidental mutation.
 */

/** Valid tier levels */
export type TierLevel = 1 | 2 | 3 | 4;

/** Tier pill style configuration */
export interface TierPillStyle {
  readonly color: string;
  readonly bgColor: string;
}

/** Canonical site URL for share links. */
export const SITE_URL = 'https://designsystemsforai.com';

export const APP_CONFIG = {
  name: 'Design Systems for AI',
  version: '0.1.0',
  author: {
    name: 'Murphy Trueman',
    email: 'murphy@murphytrueman.com',
    linkedin: 'https://linkedin.com/in/murphytrueman',
  },
} as const;

export const SCORE_CONFIG = {
  maxItemScore: 3,
  perfectScore: {
    rated: 15,
    minTotal: 40,
  },
  confettiDuration: 5000, // milliseconds
} as const;

/** Tier level (1–4) by total score. Used for results display and Brevo. */
export const TIER_THRESHOLDS = {
  tier4Min: 38, // AI-Native
  tier3Min: 28, // AI-Compatible
  tier2Min: 18, // AI-Aware
} as const;

/** Pill styles for tier badges (level 1–4). Typed for safety. */
export const TIER_PILL_STYLES: Record<TierLevel, TierPillStyle> = {
  4: { color: 'text-green-800', bgColor: 'bg-green-100' },
  3: { color: 'text-blue-800', bgColor: 'bg-blue-100' },
  2: { color: 'text-amber-800', bgColor: 'bg-amber-100' },
  1: { color: 'text-orange-800', bgColor: 'bg-orange-100' },
};

/**
 * Get tier pill style with fallback for invalid tier levels
 */
export function getTierPillStyle(level: number): TierPillStyle {
  if (level >= 1 && level <= 4 && Number.isInteger(level)) {
    return TIER_PILL_STYLES[level as TierLevel];
  }
  return TIER_PILL_STYLES[1]; // Default to tier 1 for invalid values
}

export const STORAGE_KEYS = {
  checklistState: 'ai-readiness-checklist-v2',
  cacheVersion: 'ai-readiness-checklist-version',
} as const;

export const CACHE_CONFIG = {
  version: '2.0.0',
  debounceMs: 500, // Debounce saves by 500ms
  maxCacheSize: 1024 * 1024, // 1MB max cache size
} as const;

export const UI_MESSAGES = {
  downloadReportPlaceholder: 'Download feature coming soon!',
  linkCopied: 'Link copied to clipboard!',
  ratingConfirmation: '✓ Answer saved.',
  loadError: 'Failed to load guide',
} as const;

/**
 * Encodes text for use in URLs (handles special characters safely)
 */
function safeEncodeURIComponent(text: string): string {
  if (typeof text !== 'string') return '';
  return encodeURIComponent(text);
}

export const SOCIAL_SHARE = {
  twitter: {
    baseUrl: 'https://twitter.com/intent/tweet',
    /** Share the tool (no score) – for main quiz page. */
    getShareToolText: (): string =>
      `Auditing your design system for AI-readiness? This 15-item checklist from @MurphyTrueman helps you see where you stand and what to fix.`,
    /** Share the user's results – for results page. Optional tierName (e.g. "AI-Compatible") included when provided. */
    getShareResultsText: (score: number, maxScore: number, tierName?: string): string => {
      const safeScore = typeof score === 'number' && Number.isFinite(score) ? Math.floor(score) : 0;
      const safeMaxScore = typeof maxScore === 'number' && Number.isFinite(maxScore) ? Math.floor(maxScore) : 0;
      const scorePart = tierName
        ? `Scored ${safeScore}/${safeMaxScore} — ${tierName}.`
        : `Scored ${safeScore}/${safeMaxScore}.`;
      return `Just audited our design system for AI-readiness using @MurphyTrueman's checklist. ${scorePart} What's your score?`;
    },
    /** Build the full share URL */
    getShareUrl: (text: string): string =>
      `https://twitter.com/intent/tweet?text=${safeEncodeURIComponent(text)}`,
  },
  linkedin: {
    baseUrl: 'https://www.linkedin.com/sharing/share-offsite/',
    /** Build the full share URL */
    getShareUrl: (url: string): string =>
      `https://www.linkedin.com/sharing/share-offsite/?url=${safeEncodeURIComponent(url)}`,
  },
} as const;

export const ANIMATION_DURATIONS = {
  transition: 200, // milliseconds
  accordion: 300, // milliseconds
  confetti: 5000, // milliseconds
} as const;

export const HEADER_CONFIG = {
  height: 56, // pixels
  scrollOffset: -100, // pixels for scroll positioning
} as const;

export const ACCESSIBILITY = {
  ariaLabels: {
    checkItem: 'Mark item as complete',
    uncheckItem: 'Mark item as incomplete',
    expandItem: 'Expand item details',
    collapseItem: 'Collapse item details',
    downloadReport: 'Download your progress report',
    shareResults: 'Share your results',
    rateItem: 'Rate this item',
  },
} as const;
